/* RESPONSIBLE TEAM: team-automation-foundations */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { later, cancel } from '@ember/runloop';
import { get } from 'embercom/lib/ajax';
import type Session from 'embercom/services/session';
import { hasFeature } from 'embercom/helpers/has-feature';

const LOCALIZATION_POLLING_INTERVAL = 5000; // 5 seconds
const MAX_POLLING_DURATION = 300000; // 5 minutes

interface TranslationStatus {
  locale: string;
  status: string;
  message_types: string[];
  updated_at: string;
}

interface TranslationStatusResponse {
  profile_version_id: number;
  translation_statuses: TranslationStatus[];
}

export default class FinSetupLocalizationService extends Service {
  @service declare appService: AppService;
  @service declare session: Session;
  @tracked localeStatuses: Record<string, string> = {}; // locale -> status mapping
  @tracked isLoadingTranslations = false;

  _pollingTimer: ReturnType<typeof later> | null = null;
  _pollingStartTime: number | null = null;
  _hasStartedPolling = false;
  _serviceId = Math.random().toString(36).substr(2, 9); // Debug ID

  get isAutoTranslationEnabled(): boolean {
    return hasFeature('fin-simple-setup-auto-translation', this.appService, this.session);
  }

  @action
  async startPolling() {
    // Only start polling if the auto-translation feature flag is enabled
    if (!this.isAutoTranslationEnabled) {
      return;
    }

    if (this._hasStartedPolling) {
      return;
    }
    this._hasStartedPolling = true;
    this.pollForTranslations();
  }

  @action
  stopPolling() {
    if (this._pollingTimer) {
      cancel(this._pollingTimer);
      this._pollingTimer = null;
    }
    this._hasStartedPolling = false;
    this._pollingStartTime = null;
    this.isLoadingTranslations = false;
  }

  async pollForTranslations() {
    // Stop polling if auto-translation feature is disabled
    if (!this.isAutoTranslationEnabled) {
      this.stopPolling();
      return;
    }

    // Check if we've exceeded max polling duration
    if (this._pollingStartTime && Date.now() - this._pollingStartTime > MAX_POLLING_DURATION) {
      this.isLoadingTranslations = false;
      this.stopPolling();
      return;
    }

    if (!this._pollingStartTime) {
      this._pollingStartTime = Date.now();
    }

    try {
      this.isLoadingTranslations = true;
      let statusResponse = await this.checkTranslationStatus();

      // Reset all locales to 'ready' first
      let newStatuses: Record<string, string> = {};

      // Set pending locales based on API response
      statusResponse.translation_statuses.forEach((status) => {
        newStatuses[status.locale] = status.status;
      });

      this.localeStatuses = newStatuses;

      // Check if we still have pending translations
      let hasPendingTranslations = Object.values(this.localeStatuses).some(
        (status) => status === 'pending',
      );

      if (!hasPendingTranslations) {
        // All translations are ready, stop polling but keep the flag set
        // so we don't restart polling on every hover when there's nothing to poll
        this.isLoadingTranslations = false;
        if (this._pollingTimer) {
          cancel(this._pollingTimer);
          this._pollingTimer = null;
        }
        // Don't reset _hasStartedPolling here - only reset on manual stop or destroy
      } else {
        // Still have pending translations, schedule next poll
        this.scheduleNextPoll();
      }
    } catch (error) {
      console.warn('Failed to fetch translation status:', error);
      this.scheduleNextPoll();
    }
  }

  async checkTranslationStatus(): Promise<TranslationStatusResponse> {
    let response = await get(
      `/api/ember/operator_workflows/fin_setup_profiles/${this.appService.app.id}/translation_status`,
      {},
    );
    return response;
  }

  getStatusForLocale(locale: string): string {
    return this.localeStatuses[locale] || 'ready';
  }

  isLocaleDisabled(locale: string): boolean {
    return this.getStatusForLocale(locale) === 'pending';
  }

  isLocaleLoading(locale: string): boolean {
    return this.isLoadingTranslations && this.getStatusForLocale(locale) === 'pending';
  }

  private scheduleNextPoll() {
    this._pollingTimer = later(this, this.pollForTranslations, LOCALIZATION_POLLING_INTERVAL);
  }

  willDestroy() {
    super.willDestroy();
    this.stopPolling();
  }
}

declare module '@ember/service' {
  interface Registry {
    finSetupLocalizationService: FinSetupLocalizationService;
    'fin-setup-localization-service': FinSetupLocalizationService;
  }
}
